/**
 * Accent Map — BCP 47 locale → display name + voice selection config
 *
 * Used by useVoiceInput to select the right speech recognition locale
 * for the user's preferred accent.
 *
 * @module config/accentMap
 */

const ACCENT_MAP = {
  // ── English ────────────────────────
  'en-US': { label: 'English (US)', flag: '🇺🇸', group: 'English' },
  'en-GB': { label: 'English (UK)', flag: '🇬🇧', group: 'English' },
  'en-IN': { label: 'English (India)', flag: '🇮🇳', group: 'English' },
  'en-AU': { label: 'English (Australia)', flag: '🇦🇺', group: 'English' },
  'en-ZA': { label: 'English (South Africa)', flag: '🇿🇦', group: 'English' },
  'en-NZ': { label: 'English (New Zealand)', flag: '🇳🇿', group: 'English' },
  'en-CA': { label: 'English (Canada)', flag: '🇨🇦', group: 'English' },

  // ── Hindi ──────────────────────────
  'hi-IN': { label: 'Hindi (India)', flag: '🇮🇳', group: 'Hindi' },

  // ── Spanish ────────────────────────
  'es-ES': { label: 'Spanish (Spain)', flag: '🇪🇸', group: 'Spanish' },
  'es-MX': { label: 'Spanish (Mexico)', flag: '🇲🇽', group: 'Spanish' },
  'es-AR': { label: 'Spanish (Argentina)', flag: '🇦🇷', group: 'Spanish' },

  // ── French ─────────────────────────
  'fr-FR': { label: 'French (France)', flag: '🇫🇷', group: 'French' },
  'fr-CA': { label: 'French (Canada)', flag: '🇨🇦', group: 'French' },

  // ── German ─────────────────────────
  'de-DE': { label: 'German', flag: '🇩🇪', group: 'German' },

  // ── Portuguese ─────────────────────
  'pt-BR': { label: 'Portuguese (Brazil)', flag: '🇧🇷', group: 'Portuguese' },
  'pt-PT': { label: 'Portuguese (Portugal)', flag: '🇵🇹', group: 'Portuguese' },

  // ── Arabic ─────────────────────────
  'ar-SA': { label: 'Arabic (Saudi)', flag: '🇸🇦', group: 'Arabic' },
  'ar-EG': { label: 'Arabic (Egypt)', flag: '🇪🇬', group: 'Arabic' },

  // ── East Asian ─────────────────────
  'zh-CN': { label: 'Chinese (Simplified)', flag: '🇨🇳', group: 'Chinese' },
  'zh-TW': { label: 'Chinese (Traditional)', flag: '🇹🇼', group: 'Chinese' },
  'ja-JP': { label: 'Japanese', flag: '🇯🇵', group: 'Japanese' },
  'ko-KR': { label: 'Korean', flag: '🇰🇷', group: 'Korean' },

  // ── Other ──────────────────────────
  'it-IT': { label: 'Italian', flag: '🇮🇹', group: 'Italian' },
  'ru-RU': { label: 'Russian', flag: '🇷🇺', group: 'Russian' },
  'nl-NL': { label: 'Dutch', flag: '🇳🇱', group: 'Dutch' },
  'pl-PL': { label: 'Polish', flag: '🇵🇱', group: 'Polish' },
  'tr-TR': { label: 'Turkish', flag: '🇹🇷', group: 'Turkish' },
  'th-TH': { label: 'Thai', flag: '🇹🇭', group: 'Thai' },
  'vi-VN': { label: 'Vietnamese', flag: '🇻🇳', group: 'Vietnamese' },
  'id-ID': { label: 'Indonesian', flag: '🇮🇩', group: 'Indonesian' },
  'ms-MY': { label: 'Malay', flag: '🇲🇾', group: 'Malay' },
  'sv-SE': { label: 'Swedish', flag: '🇸🇪', group: 'Swedish' },
  'da-DK': { label: 'Danish', flag: '🇩🇰', group: 'Danish' },
  'nb-NO': { label: 'Norwegian', flag: '🇳🇴', group: 'Norwegian' },
  'fi-FI': { label: 'Finnish', flag: '🇫🇮', group: 'Finnish' },
};

/**
 * Get all supported accent groups (for grouped display in settings).
 * @returns {{ group: string, locales: { code: string, label: string, flag: string }[] }[]}
 */
export function getAccentGroups() {
  const groups = {};
  for (const [code, info] of Object.entries(ACCENT_MAP)) {
    if (!groups[info.group]) groups[info.group] = [];
    groups[info.group].push({ code, label: info.label, flag: info.flag });
  }
  return Object.entries(groups).map(([group, locales]) => ({ group, locales }));
}

/**
 * Get display info for a locale.
 * @param {string} locale
 * @returns {{ label: string, flag: string, group: string } | null}
 */
export function getAccentInfo(locale) {
  return ACCENT_MAP[locale] || null;
}

/**
 * Auto-detect the best locale from the user's environment.
 * Priority: user preference → navigator.language → 'en-US'
 *
 * @param {Object} [user] — User object with voicePreferences
 * @returns {string} BCP 47 locale
 */
export function detectLocale(user) {
  // 1. User's explicit voice preference
  if (user?.voicePreferences?.locale && ACCENT_MAP[user.voicePreferences.locale]) {
    return user.voicePreferences.locale;
  }

  // 2. User's region setting
  if (user?.region) {
    const regionMatch = Object.keys(ACCENT_MAP).find(k => k.endsWith(`-${user.region}`));
    if (regionMatch) return regionMatch;
  }

  // 3. Browser language
  if (typeof navigator !== 'undefined' && navigator.language) {
    const browserLang = navigator.language;
    // Exact match
    if (ACCENT_MAP[browserLang]) return browserLang;
    // Language-only match
    const langOnly = browserLang.split('-')[0];
    const langMatch = Object.keys(ACCENT_MAP).find(k => k.startsWith(langOnly + '-'));
    if (langMatch) return langMatch;
  }

  // 4. Default
  return 'en-US';
}

export default ACCENT_MAP;
